import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { toMoneyInput, zeroMoney } from '@rematter/modules/trade/utils/money';
import { toWeightInput } from '@rematter/modules/trade/utils/weight';
import { Button, Footer } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';

import { ReconciliationSummary } from './ReconciliationSummary';

import {
  $materialsReconciliationForm,
  $updateLoadReconciliation,
} from '~/modules/trade/_state/loadReconciliation.state';
import { WeightUnitType } from '~/modules/trade/types/load';

type ReconciliationFooterProps = {
  className?: string;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  onDiscard?: VoidFunction;
};

export const ReconciliationFooter: React.FC<ReconciliationFooterProps> = ({
  className,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  onDiscard,
}) => {
  const t = useTranslations();

  const updateReconciliationMutation = useSetAtom($updateLoadReconciliation);
  const formState = useAtomValue($materialsReconciliationForm);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSaveLoadReconciliation = React.useCallback(async () => {
    if (!formState.loadId || !formState.loadReconciliationId) {
      console.error('Load ID or reconciliation ID is missing');
      return;
    }

    setIsSubmitting(true);
    try {
      const receivedLineItems =
        formState.receivedLineItems.length > 0
          ? formState.receivedLineItems.map((item) => ({
              id: item.id?.startsWith('temp') ? undefined : item.id,
              itemId: item.itemId,
              loadLineItemId: item.loadLineItemId,
              materialType: item.materialType ?? 'Raw',
              grossWeight: item.grossWeight
                ? {
                    value: item.grossWeight.value,
                    unit: item.grossWeight.unit as Unit,
                  }
                : { value: 0, unit: enumUnit.Pounds },
              tareWeight: item.tareWeight
                ? {
                    value: item.tareWeight.value,
                    unit: item.tareWeight.unit as Unit,
                  }
                : { value: 0, unit: enumUnit.Pounds },
              unitPrice: toMoneyInput(item.unitPrice),
              pricingUnit: item.pricingUnit
                ? item.pricingUnit
                : enumUnit.Pounds,
              notes: item.notes,
            }))
          : undefined;

      const deductions =
        formState.deductions.length > 0
          ? formState.deductions.map((deduction) => ({
              id: deduction.id?.startsWith('temp') ? undefined : deduction.id,
              receivedLineItemId: deduction.receivedLineItemId,
              deductionTypeId: deduction.deductionTypeId,
              priceQuantity: deduction.priceQuantity
                ? toMoneyInput(deduction.priceQuantity)
                : undefined,
              weightQuantity: toWeightInput(deduction.weightQuantity),
            }))
          : undefined;

      const freightCharges =
        formState.freightCharges.length > 0
          ? formState.freightCharges.map((charge) => ({
              id: charge.id?.startsWith('temp') ? undefined : charge.id,
              hauler: charge.hauler,
              freightLane: charge.freightLane,
              deliveryNumber: charge.deliveryNumber,
              accruedAmount: charge.accruedAmount
                ? toMoneyInput(charge.accruedAmount)
                : zeroMoney(),
              chargebackAmount: charge.chargebackAmount
                ? toMoneyInput(charge.chargebackAmount)
                : zeroMoney(),
              notes: charge.notes ?? '',
            }))
          : undefined;

      // Update the reconciliation with form data
      await updateReconciliationMutation({
        loadId: formState.loadId,
        receivedLineItems,
        deductions,
        freightCharges,
        ticketNumber: formState.ticketNumber,
        loadReceivedDate: formState.loadReceivedDate,
        ticketIssueDate: formState.ticketIssueDate,
        millReceivedWeightPostDate: formState.millReceivedWeightPostDate,
        externalNotes: formState.externalNotes,
        internalNotes: formState.internalNotes,
      });
    } catch (error) {
      // Error is already handled by the mutation
      console.error('Reconciliation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, updateReconciliationMutation]);

  return (
    <Footer
      className={cn(
        '@ReconciliationFooter',
        'sticky bottom-0 rounded-b-lg border-t border-neutral/10 bg-white',
        'flex w-full flex-col items-end justify-end gap-3 px-9 py-4',
        className,
      )}
      displayShadow
    >
      <ReconciliationSummary
        weightUnit={weightUnit}
        facilityDefaultUnit={facilityDefaultUnit}
      />

      <div className="flex w-full justify-end gap-3 border-t border-neutral/10 pt-4">
        <Button
          color="neutral"
          variant="outline"
          onPress={onDiscard}
          isPending={isSubmitting}
        >
          {t.materialsReconciliation.footer.discard}
        </Button>
        <Button
          color="primary"
          variant="solid"
          onPress={handleSaveLoadReconciliation}
          isPending={isSubmitting}
          isLoading={isSubmitting}
        >
          {t.materialsReconciliation.footer.saveChanges}
        </Button>
      </div>
    </Footer>
  );
};
